<template>
  <div class="mobile-tab-bar" :class="{ hidden: !showBar }">
    <div
      v-for="item in tabItems"
      :key="item.path"
      class="tab-item"
      :class="{ active: isActive(item) }"
      @click="handleClick(item)"
    >
      <svg-icon v-if="item.icon" :icon-class="item.icon" class="tab-icon" />
      <span class="tab-label">{{ item.title }}</span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'MobileTabBar',
  data() {
    return {
      showBar: true,
      lastScrollTop: 0
    }
  },
  computed: {
    ...mapGetters(['routers']),
    tabItems() {
      const MAX_TABS = 5
      const MIN_TABS = 4

      // 将路由转为 Tab 项
      const routeToTab = (r) => {
        let resolvedPath = r.path
        if (resolvedPath === '' || resolvedPath === '/') {
          resolvedPath = (r.redirect && r.redirect !== 'noredirect') ? r.redirect : '/home'
        }
        let title = (r.meta && r.meta.title) || r.name || ''
        let icon = (r.meta && r.meta.icon) || ''
        const titleMap = { '小程序': '订单' }
        const iconMap = { 'product': 'table' }
        if (titleMap[title]) title = titleMap[title]
        if (iconMap[icon]) icon = iconMap[icon]
        if (title === '首页') {
          title = '排名'
          icon = 'dashboard'
        }
        return { path: resolvedPath, title, icon }
      }

      // 可见的一级路由（有可见子菜单）
      const visibleRoutes = this.routers
        .filter(r => !r.hidden && r.children && r.children.some(c => !c.hidden))

      // 优先级分组：核心菜单 vs 备选菜单
      const skipTitles = ['广告', '营销', 'app', 'APP']
      const priority = []
      const fallback = []
      const seen = new Set()

      for (const r of visibleRoutes) {
        const title = (r.meta && r.meta.title) || r.name || ''
        if (!title || seen.has(title)) continue
        seen.add(title)
        if (skipTitles.includes(title)) {
          fallback.push(r)
        } else {
          priority.push(r)
        }
      }

      // 先从核心菜单取 Tab（最多 MAX_TABS - 1，给提现管理留一个位置）
      const items = priority.slice(0, MAX_TABS - 1).map(routeToTab)

      // 提现管理：仅当用户有权限时追加
      const hasWithdraw = this.routers.some(r =>
        r.children && r.children.some(c => c.name === 'withdraw' && !c.hidden)
      )
      if (hasWithdraw && items.length < MAX_TABS) {
        items.push({
          path: '/bms/withdraw',
          title: '提现管理',
          icon: 'total-today'
        })
      }

      // 如果 Tab 数量不足，从备选菜单中补充
      if (items.length < MIN_TABS) {
        for (const r of fallback) {
          if (items.length >= MAX_TABS) break
          const title = (r.meta && r.meta.title) || r.name || ''
          if (!title) continue
          items.push(routeToTab(r))
        }
      }

      // 如果还不够，从剩余核心菜单继续补充
      if (items.length < MIN_TABS) {
        for (const r of priority.slice(MAX_TABS - 1)) {
          if (items.length >= MAX_TABS) break
          const title = (r.meta && r.meta.title) || r.name || ''
          if (!title) continue
          items.push(routeToTab(r))
        }
      }

      return items
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      // 兼容多种滚动容器：document.scrollingElement > window > 回退
      const el = document.scrollingElement || document.documentElement
      const currentScroll = el.scrollTop || window.scrollY || 0
      // 滚动到顶部或滚动距离很小时始终显示
      if (currentScroll <= 10) {
        this.showBar = true
      } else if (currentScroll > this.lastScrollTop + 5) {
        // 向下滚动（内容上滑）→ 隐藏
        this.showBar = false
      } else if (currentScroll < this.lastScrollTop - 5) {
        // 向上滚动（内容下滑）→ 显示
        this.showBar = true
      }
      this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll
    },
    isActive(item) {
      const routePath = this.$route.path
      // 完整路径（如 /bms/withdraw）精确匹配
      if (item.path.split('/').length > 2) {
        return routePath.startsWith(item.path)
      }
      // 一级模块（如 /bms）只匹配到该模块下，但不匹配其子路由被其他 Tab 单独占用的路径
      // 提现管理 /bms/withdraw 已被单独 Tab 占用，账单管理不应该在 /bms/withdraw 时高亮
      const prefix = item.path + '/'
      if (!routePath.startsWith(prefix)) return false
      // 检查是否有其他 Tab 的 path 也以当前 item.path 开头且更精确匹配
      const otherTabs = this.tabItems.filter(t => t.path !== item.path)
      const overridden = otherTabs.some(t => routePath.startsWith(t.path + '/') || routePath === t.path)
      return !overridden
    },
    handleClick(item) {
      if (this.isActive(item)) return
      // 如果是完整路径（如 /bms/withdraw），直接跳转
      if (item.path.indexOf('/') !== -1 && !this.routers.find(r => r.path === item.path)) {
        this.$router.push({ path: item.path })
        return
      }
      // 跳转到该模块第一个可见子路由
      const route = this.routers.find(r => r.path === item.path)
      if (route && route.children) {
        const firstChild = route.children.find(c => !c.hidden)
        if (firstChild) {
          this.$router.push({ path: item.path + '/' + firstChild.path })
        } else if (route.children.length > 0) {
          this.$router.push({ path: item.path + '/' + route.children[0].path })
        } else {
          this.$router.push({ path: item.path })
        }
      }
    }
  }
};
</script>

<style scoped>
.mobile-tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1002;
  height: 56px;
  background: #fff;
  border-top: 1px solid #e8eaed;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding-bottom: env(safe-area-inset-bottom, 0);
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.04);
  transform: translateY(0);
  transition: transform 0.3s ease;
}

.mobile-tab-bar.hidden {
  transform: translateY(100%);
}

.tab-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  transition: color .2s;
  color: #86909c;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.tab-item:active {
  opacity: 0.7;
}

.tab-item.active {
  color: #3b6ff5;
}

.tab-icon {
  width: 22px;
  height: 22px;
  margin-bottom: 2px;
}

.tab-label {
  font-size: 11px;
  line-height: 1;
}
</style>



// WEBPACK FOOTER //
// src/views/layout/components/MobileTabBar.vue